import { Check, ChevronRight, ShieldCheck, ShoppingBag, Tag, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CartItemsSection from "./CartItemsSection/CartItemsSection";
import CartYourItems from "./CartYourItems/CartYourItems";
import "./CartPage.css";
import "./CartPageMobile.css";
import { CartItem, CartType, CouponType } from "../../../Redux/custmoer_cart_service/types";
import { fetchAvailableCoupons, applyCoupon, removeCoupon } from "../../../Redux/custmoer_cart_service/slice";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { RootState } from "../../../store";
import HomeNavbar from "../../globalcomponents/HomeNavbar/HomeNavbar";
import { useCart } from "../../../context/CartContext";
import MobileDetailNavbar from "../../globalcomponents/mobile/MobileDetailNavbar/MobileDetailNavbar";

type CartPromoValues = Record<string, string>;
type CartPromoApplied = Record<string, boolean>;

const formatPrice = (value: number) =>
    new Intl.NumberFormat("en-IN").format(Math.max(0, Math.round(value)));

const CartPage = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const {
        cart,
        cartItems,
        cartCount,
        removeItemFromCart,
        updateItemQuantity,
        fetchCart,
        applyVoucherCode,
        applyReferralCode,
        removeReferralCode,
        applySalarCodeValue,
        removeSalarCodeValue,
        savedForLaterItems,
        fetchSavedForLater,
        saveItemForLater,
    } = useCart();

    const { availableCoupons, availableCouponsLoading } = useAppSelector(
        (state: RootState) => state.customerCart
    );

    const [selectedItems, setSelectedItems] = useState<string[]>([]);
    const [voucherInputs, setVoucherInputs] = useState<CartPromoValues>({});
    const [referralInputs, setReferralInputs] = useState<CartPromoValues>({});
    const [salarCodeInputs, setSalarCodeInputs] = useState<CartPromoValues>({});
    const [voucherApplied, setVoucherApplied] = useState<CartPromoApplied>({});
    const [referralApplied, setReferralApplied] = useState<CartPromoApplied>({});
    const [salarCodeApplied, setSalarCodeApplied] = useState<CartPromoApplied>({});

    // Sidebar Coupon State
    const [manualCouponCode, setManualCouponCode] = useState("");
    const [applyingCouponCode, setApplyingCouponCode] = useState<string | null>(null);
    const [removingCoupon, setRemovingCoupon] = useState(false);

    // Referral Code Confirmation Modal State
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingReferralCode, setPendingReferralCode] = useState("");

    // Fetch Cart + saved-for-later items on mount
    useEffect(() => {
        fetchCart(CartType.ECOMMERCE);
        fetchSavedForLater(CartType.ECOMMERCE);
    }, []);

    // Fetch Available Coupons whenever cart items or total updates
    useEffect(() => {
        if (cartItems.length > 0) {
            dispatch(fetchAvailableCoupons(CartType.ECOMMERCE));
        }
    }, [dispatch, cartItems.length, cart?.itemsTotal]);

    // Keep inputs in sync with applied server codes
    useEffect(() => {
        if (cart?.invitationCode) {
            const nextVal: CartPromoValues = {};
            const nextApplied: CartPromoApplied = {};
            cartItems.forEach((i) => {
                nextVal[i.id] = cart.invitationCode || "";
                nextApplied[i.id] = true;
            });
            setReferralInputs((prev) => ({ ...nextVal, ...prev }));
            setReferralApplied((prev) => ({ ...nextApplied, ...prev }));
        } else {
            setReferralApplied({});
        }

        if (cart?.salarCode) {
            const nextVal: CartPromoValues = {};
            const nextApplied: CartPromoApplied = {};
            cartItems.forEach((i) => {
                nextVal[i.id] = cart.salarCode || "";
                nextApplied[i.id] = true;
            });
            setSalarCodeInputs((prev) => ({ ...nextVal, ...prev }));
            setSalarCodeApplied((prev) => ({ ...nextApplied, ...prev }));
        } else {
            setSalarCodeApplied({});
        }

        if (cart?.voucherAppliedAmount && cart.voucherAppliedAmount > 0) {
            const nextApplied: CartPromoApplied = {};
            cartItems.forEach((i) => {
                nextApplied[i.id] = true;
            });
            setVoucherApplied((prev) => ({ ...nextApplied, ...prev }));
        }
    }, [cart?.invitationCode, cart?.salarCode, cart?.voucherAppliedAmount, cartItems]);

    // Use item.id (the cart-item's own unique ID) as the selection key
    const knownItemIdsRef = useRef<Set<string>>(new Set());
    useEffect(() => {
        const currentIds = new Set<string>(cartItems.map((item) => item.id));
        setSelectedItems((prev) => {
            const valid = prev.filter((id) => currentIds.has(id));
            const newlyAdded = cartItems
                .map((item) => item.id)
                .filter((id) => !knownItemIdsRef.current.has(id));
            return Array.from(new Set([...valid, ...newlyAdded]));
        });
        knownItemIdsRef.current = currentIds;
    }, [cartItems]);

    const allSelected = cartItems.length > 0 && selectedItems.length === cartItems.length;

    const toggleSelectAll = () => {
        if (allSelected) {
            setSelectedItems([]);
            return;
        }
        setSelectedItems(cartItems.map((item) => item.id));
    };

    const toggleItem = (itemId: string) => {
        setSelectedItems((prev) =>
            prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
        );
    };

    const selectedCartItems = useMemo(
        () => cartItems.filter((item) => selectedItems.includes(item.id)),
        [cartItems, selectedItems]
    );

    // Totals calculations preferring backend values when all items are selected
    const localItemTotal = useMemo(
        () => selectedCartItems.reduce((total, item) => total + item.unitPrice * item.quantity, 0),
        [selectedCartItems]
    );

    const itemTotal =
        allSelected && cart?.itemsTotal && cart.itemsTotal > 0
            ? cart.itemsTotal
            : localItemTotal;

    const totalMrp = useMemo(
        () =>
            selectedCartItems.reduce(
                (total, item) => total + (item.metadata?.mrp ?? item.unitPrice) * item.quantity,
                0
            ),
        [selectedCartItems]
    );

    const productSavings = Math.max(0, totalMrp - itemTotal);

    // Backend-backed promo values
    const voucherDiscount = Number(cart?.voucherAppliedAmount || 0);
    const salarCodeDiscount = Number(cart?.salarCodeDiscountAmount || 0);
    const couponDiscount = Number(cart?.couponDiscountAmount || 0);
    const deliveryPartnerFee = Number(cart?.deliveryCharges || (selectedCartItems.length > 0 ? 30 : 0));
    const platformFee = Number(cart?.platformFee || (selectedCartItems.length > 0 ? 5 : 0));

    const grandTotal =
        allSelected && cart?.grandTotal && cart.grandTotal > 0
            ? cart.grandTotal
            : Math.max(
                  0,
                  itemTotal -
                      voucherDiscount -
                      salarCodeDiscount -
                      couponDiscount +
                      deliveryPartnerFee +
                      platformFee
              );

    const totalSavings =
        productSavings +
        voucherDiscount +
        salarCodeDiscount +
        couponDiscount +
        Number(cart?.savedAmount || 0);

    const handleDelete = (itemId: string) => {
        removeItemFromCart(itemId);
        setSelectedItems((prev) => prev.filter((id) => id !== itemId));
    };

    const handleDecrease = (item: CartItem) => {
        updateItemQuantity(item.id, item.quantity - 1);
        if (item.quantity === 1) {
            setSelectedItems((prev) => prev.filter((id) => id !== item.id));
        }
    };

    const handleSaveForLater = async (item: CartItem) => {
        try {
            await saveItemForLater(item.id);
            setSelectedItems((prev) => prev.filter((id) => id !== item.id));
            toast.success("Item saved for later");
        } catch (err: any) {
            toast.error(err || "Failed to save item for later");
        }
    };

    // Promotion actions
    const applyVoucher = (itemId: string) => {
        const value = voucherInputs[itemId]?.trim();
        if (!value) {
            toast.error("Please enter a shopping voucher code");
            return;
        }
        applyVoucherCode(value);
        setVoucherApplied((prev) => ({ ...prev, [itemId]: true }));
        toast.success(`Voucher "${value}" submitted!`);
    };

    const triggerReferralConfirm = (code: string) => {
        const trimmed = code.trim();
        if (!trimmed) {
            toast.error("Please enter an invitation / referral code");
            return;
        }
        const registerIdRegex = /^S\d{2}[A-Z]\d{2}\d+$/;
        if (!registerIdRegex.test(trimmed)) {
            toast.error("Invalid Invitation Code format. Example: S12A345");
            return;
        }
        setPendingReferralCode(trimmed);
        setShowConfirmModal(true);
    };

    const confirmApplyReferral = () => {
        if (!pendingReferralCode) return;
        applyReferralCode(pendingReferralCode);
        setShowConfirmModal(false);
        toast.success(`Invitation code "${pendingReferralCode}" applied!`);
    };

    const handleRemoveReferral = () => {
        removeReferralCode();
        setReferralInputs({});
        setReferralApplied({});
        toast.info("Referral code removed");
    };

    const applySalarCode = (itemId: string) => {
        const value = salarCodeInputs[itemId]?.trim();
        if (!value) {
            toast.error("Please enter a Salar code");
            return;
        }
        applySalarCodeValue(value);
        setSalarCodeApplied((prev) => ({ ...prev, [itemId]: true }));
        toast.success(`Salar code "${value}" applied!`);
    };

    const handleRemoveSalarCode = () => {
        removeSalarCodeValue();
        setSalarCodeInputs({});
        setSalarCodeApplied({});
        toast.info("Salar code removed");
    };

    // Sidebar Coupon actions
    const handleApplySidebarCoupon = async (code: string) => {
        const trimmed = code.trim();
        if (!trimmed) return;
        setApplyingCouponCode(trimmed);
        try {
            await dispatch(
                applyCoupon({
                    data: { code: trimmed },
                    cartType: CartType.ECOMMERCE,
                })
            ).unwrap();
            toast.success(`Coupon "${trimmed}" applied successfully!`);
            setManualCouponCode("");
            dispatch(fetchAvailableCoupons(CartType.ECOMMERCE));
        } catch (err: any) {
            toast.error(err || "Failed to apply coupon");
        } finally {
            setApplyingCouponCode(null);
        }
    };

    const handleRemoveSidebarCoupon = async () => {
        setRemovingCoupon(true);
        try {
            await dispatch(removeCoupon(CartType.ECOMMERCE)).unwrap();
            toast.info("Coupon removed");
            dispatch(fetchAvailableCoupons(CartType.ECOMMERCE));
        } catch (err: any) {
            toast.error(err || "Failed to remove coupon");
        } finally {
            setRemovingCoupon(false);
        }
    };

    const discountLabel = (type: string, value: number) => {
        if (type === "FLAT") return `₹${value} OFF`;
        if (type === "PERCENTAGE") return `${value}% OFF`;
        return "Free Delivery";
    };

    const handleProceedToBuy = () => {
        if (selectedCartItems.length === 0) return;
        navigate("/Checkout");
    };

    if (cartItems.length === 0 && savedForLaterItems.length === 0) {
        return (
            <>
                <div className="cart-desktop-navbar"><HomeNavbar variant="home" /></div>
                <MobileDetailNavbar cartCount={cartCount} />
                <main className="cart-page cart-page-empty">
                    <div className="cart-empty-card">
                        <div className="cart-empty-icon">
                            <ShoppingBag size={38} strokeWidth={1.7} />
                        </div>
                        <span>YOUR SALAR CART</span>
                        <h1>Your cart is empty</h1>
                        <p>
                            Products you add from fashion, electronics, groceries, home appliances
                            and recommendations will appear here.
                        </p>
                        <button type="button" onClick={() => navigate("/HomePage")}>
                            Continue Shopping <ChevronRight size={17} />
                        </button>
                    </div>
                    <CartYourItems savedItems={savedForLaterItems} />
                </main>
            </>
        );
    }

    return (
        <>
            <div className="cart-desktop-navbar"><HomeNavbar variant="home" /></div>
            <MobileDetailNavbar cartCount={cartCount} />
            <main className="cart-page">
                <div className="cart-page-container">
                    <div className="cart-left-column">
                        <section className="cart-main">
                            <CartItemsSection
                                cartItems={cartItems}
                                selectedItems={selectedItems}
                                selectedCartItemsCount={selectedCartItems.length}
                                allSelected={allSelected}
                                itemTotal={itemTotal}
                                toggleSelectAll={toggleSelectAll}
                                toggleItem={toggleItem}
                                handleDecrease={handleDecrease}
                                increaseQuantity={(itemId: string) =>
                                    updateItemQuantity(
                                        itemId,
                                        (cartItems.find((i) => i.id === itemId)?.quantity ?? 1) + 1
                                    )
                                }
                                handleDelete={handleDelete}
                                handleSaveForLater={handleSaveForLater}
                                voucherInputs={voucherInputs}
                                setVoucherInputs={setVoucherInputs}
                                referralInputs={referralInputs}
                                setReferralInputs={setReferralInputs}
                                salarCodeInputs={salarCodeInputs}
                                setSalarCodeInputs={setSalarCodeInputs}
                                voucherApplied={voucherApplied}
                                setVoucherApplied={setVoucherApplied}
                                referralApplied={referralApplied}
                                setReferralApplied={setReferralApplied}
                                salarCodeApplied={salarCodeApplied}
                                setSalarCodeApplied={setSalarCodeApplied}
                                applyVoucher={applyVoucher}
                                applyReferral={(itemId: string) =>
                                    triggerReferralConfirm(referralInputs[itemId] || "")
                                }
                                applySalarCode={applySalarCode}
                                removeReferral={handleRemoveReferral}
                                removeSalarCode={handleRemoveSalarCode}
                                appliedInvitationCode={cart?.invitationCode || null}
                                appliedSalarCode={cart?.salarCode || null}
                                appliedVoucherAmount={voucherDiscount}
                            />
                        </section>
                        <CartYourItems savedItems={savedForLaterItems} />
                    </div>

                    <aside className="cart-summary-column">
                        <div className="cart-summary">
                            <div className="cart-summary-head">
                                <div>
                                    <span>ORDER SUMMARY</span>
                                    <h2>Price Details</h2>
                                </div>
                                <ShieldCheck size={22} />
                            </div>

                            <div className="cart-price-breakdown">
                                <div>
                                    <span>
                                        Items total
                                        {productSavings > 0 && (
                                            <small>
                                                You saved ₹{formatPrice(productSavings)} on products
                                            </small>
                                        )}
                                    </span>
                                    <strong>₹{formatPrice(itemTotal)}</strong>
                                </div>

                                {voucherDiscount > 0 && (
                                    <div className="discount-row">
                                        <span>Shopping voucher applied</span>
                                        <strong>- ₹{formatPrice(voucherDiscount)}</strong>
                                    </div>
                                )}

                                {cart?.invitationCode && (
                                    <div className="discount-row">
                                        <span>
                                            Invitation code <small>{cart.invitationCode}</small>
                                        </span>
                                        <strong>Applied</strong>
                                    </div>
                                )}

                                {salarCodeDiscount > 0 && (
                                    <div className="discount-row">
                                        <span>
                                            Salar code <small>{cart?.salarCode}</small>
                                        </span>
                                        <strong>- ₹{formatPrice(salarCodeDiscount)}</strong>
                                    </div>
                                )}

                                {couponDiscount > 0 && (
                                    <div className="discount-row">
                                        <span>
                                            Coupon discount <small>{cart?.couponCode}</small>
                                        </span>
                                        <strong>- ₹{formatPrice(couponDiscount)}</strong>
                                    </div>
                                )}

                                <div>
                                    <span>Delivery partner fee</span>
                                    <strong>
                                        {deliveryPartnerFee > 0
                                            ? `₹${formatPrice(deliveryPartnerFee)}`
                                            : "FREE"}
                                    </strong>
                                </div>

                                <div>
                                    <span>Handling charge</span>
                                    <strong>₹{formatPrice(platformFee)}</strong>
                                </div>
                            </div>

                            <div className="cart-grand-total">
                                <span>Grand total</span>
                                <strong>₹{formatPrice(grandTotal)}</strong>
                            </div>

                            {totalSavings > 0 && (
                                <div className="cart-total-saving">
                                    <div>
                                        <Check size={16} />
                                        <span>Your total savings</span>
                                    </div>
                                    <strong>₹{formatPrice(totalSavings)}</strong>
                                </div>
                            )}

                            {/* Sidebar Coupons Section */}
                            <div className="cart-sidebar-coupons-section">
                                <div className="cart-sidebar-coupons-header">
                                    <span>COUPONS &amp; OFFERS</span>
                                    <Tag size={15} color="#173c72" />
                                </div>

                                {cart?.couponCode && (
                                    <div className="cart-coupon-applied-alert">
                                        <div>
                                            Applied: <strong>{cart.couponCode}</strong>
                                            {couponDiscount > 0 && ` (-₹${formatPrice(couponDiscount)})`}
                                        </div>
                                        <button
                                            type="button"
                                            className="cart-coupon-remove-icon-btn"
                                            onClick={handleRemoveSidebarCoupon}
                                            disabled={removingCoupon}
                                            title="Remove Coupon"
                                        >
                                            <Trash2 size={13} />
                                        </button>
                                    </div>
                                )}

                                <div className="cart-sidebar-coupon-input">
                                    <input
                                        type="text"
                                        placeholder="Enter coupon code"
                                        value={manualCouponCode}
                                        disabled={Boolean(cart?.couponCode)}
                                        onChange={(e) => setManualCouponCode(e.target.value.toUpperCase())}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && !cart?.couponCode) {
                                                handleApplySidebarCoupon(manualCouponCode);
                                            }
                                        }}
                                    />
                                    <button
                                        type="button"
                                        disabled={
                                            !manualCouponCode.trim() ||
                                            Boolean(cart?.couponCode) ||
                                            Boolean(applyingCouponCode)
                                        }
                                        onClick={() => handleApplySidebarCoupon(manualCouponCode)}
                                    >
                                        {applyingCouponCode ? "..." : "Apply"}
                                    </button>
                                </div>

                                {availableCouponsLoading ? (
                                    <p style={{ fontSize: "10px", color: "#64748b", margin: "4px 0" }}>
                                        Loading available coupons…
                                    </p>
                                ) : availableCoupons && availableCoupons.length > 0 ? (
                                    <div className="cart-coupon-list">
                                        {availableCoupons.map((c) => {
                                            const isApplied =
                                                cart?.couponCode?.toUpperCase() ===
                                                c.couponCode?.toUpperCase();
                                            return (
                                                <div key={c.couponId} className="cart-coupon-card">
                                                    <div className="cart-coupon-info">
                                                        <span className="cart-coupon-code">
                                                            {c.couponCode}
                                                        </span>
                                                        <span className="cart-coupon-desc">
                                                            {c.couponTitle} ·{" "}
                                                            {discountLabel(c.discountType, c.discountValue)}
                                                        </span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className={`cart-coupon-apply-btn ${
                                                            isApplied ? "cart-coupon-applied" : ""
                                                        }`}
                                                        disabled={
                                                            isApplied ||
                                                            Boolean(cart?.couponCode) ||
                                                            applyingCouponCode === c.couponCode
                                                        }
                                                        onClick={() => handleApplySidebarCoupon(c.couponCode)}
                                                    >
                                                        {isApplied
                                                            ? "Applied"
                                                            : applyingCouponCode === c.couponCode
                                                            ? "..."
                                                            : "Apply"}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : null}
                            </div>

                            <button
                                type="button"
                                className="cart-checkout"
                                disabled={selectedCartItems.length === 0}
                                onClick={handleProceedToBuy}
                            >
                                Proceed to Buy <ChevronRight size={17} />
                            </button>

                            <p className="cart-checkout-note">
                                <ShieldCheck size={14} /> Secure checkout powered by Salar
                            </p>
                        </div>
                    </aside>
                </div>
            </main>

            {/* Referral Code Confirmation Modal */}
            {showConfirmModal && (
                <div className="cart-modal-overlay" onClick={() => setShowConfirmModal(false)}>
                    <div className="cart-modal-card" onClick={(e) => e.stopPropagation()}>
                        <div className="cart-modal-header">
                            <h3>Confirm Invitation Code</h3>
                            <button
                                type="button"
                                className="cart-modal-close"
                                onClick={() => setShowConfirmModal(false)}
                            >
                                &times;
                            </button>
                        </div>
                        <div className="cart-modal-body">
                            Are you sure you want to apply invitation code{" "}
                            <span className="cart-modal-code">{pendingReferralCode}</span>?
                        </div>
                        <div className="cart-modal-actions">
                            <button
                                type="button"
                                className="cart-modal-btn-cancel"
                                onClick={() => setShowConfirmModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="cart-modal-btn-confirm"
                                onClick={confirmApplyReferral}
                            >
                                Confirm &amp; Apply
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CartPage;